import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {bgColorRequester, bgColorSpender, ezprOrange} from '../../_helpers';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {useProperty} from '@olympeio/core';
import ThreeDotsMenu from '../menus/ThreeDotsMenu.jsx';
import {Checkbox, FormControlLabel, IconButton, Typography} from '@mui/material';
import {PurchaseRequestController} from '../../../controllers/PurchaseRequestController';
import SearchIcon from '@mui/icons-material/Search';
import {DefineSupplierPopup} from '../dialogs/DefineSupplierPopup.jsx';
import {OneTimeVendor} from '../../../models/OneTimeVendor';
import {Config, BrickContext} from 'olympe';
import {LAMBDA_NAMES, mandatoryFieldsForRequester, mandatoryFieldsForSpender, SOURCES} from '../../../utils/constants';
import {IconWithTooltip} from './IconWithTooltip.jsx';
import {fetchAndProcessIncoterms} from '../../../utils/incotermsHelpers';
import {getVendorCodes} from "../../../utils/helpers";
import {useFieldEditability} from './useFieldEditability.js';

/**
 * @param {TabSelector.propTypes} props
 * @return {JSX.Element}
 */
export function TabSelector(props) {
    const getSupplierPurchaseOrgLink = props.context.get(LAMBDA_NAMES.GET_SUPPLIER_PURCHASE_ORG_LINK);
    const getAllIncoterms = props.context.get(LAMBDA_NAMES.GET_ALL_INCOTERMS);

    const useRequestStore = requestStores.get(props.prTag);
    const currentTab = useProperty(props.context, 'Current tab', false);
    const vendorCode = useRequestStore((state) => state.vendorCode);
    const vendorName = useRequestStore((state) => state.vendorName);
    const requester = useRequestStore((state) => state.requester);
    const spender = useRequestStore((state) => state.spender);
    const status = useRequestStore((state) => state.status);
    const [highlightMandatoryFieldForRequester, setHighlightMandatoryFieldForRequester] = useRequestStore((state) => [state.highlightMandatoryFieldForRequester, state.setHighlightMandatoryFieldForRequester]);
    const [highlightMandatoryFieldForSpender, setHighlightMandatoryFieldForSpender] = useRequestStore((state) => [state.highlightMandatoryFieldForSpender, state.setHighlightMandatoryFieldForSpender]);
    const userHasAccessToProperties = useRequestStore((state) => state.userHasAccessToProperties);
    const setOTVName = useRequestStore((state) => state.setOTVName);
    const setOTVAddress = useRequestStore((state) => state.setOTVAddress);
    const setOTVPhoneNumber = useRequestStore((state) => state.setOTVPhoneNumber);
    const setSupplierType = useRequestStore((state) => state.setSupplierType);
    const setVendorName = useRequestStore((state) => state.setVendorName);
    const setVendorCode = useRequestStore((state) => state.setVendorCode);
    const setLoadingMaterials = useRequestStore((state) => state.setLoadingMaterials);
    const editableSupplier = useRequestStore((state) => state.editableSupplier);
    const prLocked = useRequestStore((state) => state.prLocked);
    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});
    const companyCode = useRequestStore((state) => state.companyCode);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const setIncoterms = useRequestStore((state) => state.setIncoterms);
    const source = useRequestStore((state) => state.source);
    const isCandex = source === SOURCES.CANDEX;

    const [defineSupplierPopupOpen, setDefineSupplierPopupOpen] = useState(false);

    useEffect(() => {
        const handleCustomEvent = (event) => {
            setDefineSupplierPopupOpen(event.detail.prTag === props.prTag);
        };
        window?.addEventListener('openVendorDialog', handleCustomEvent);

        return () => {
            window?.removeEventListener('openVendorDialog', handleCustomEvent);
        };
    }, []);

    const handleCancelDefineSupplierPopup = useCallback(() => {
        setDefineSupplierPopupOpen(false);
    }, []);

    const handleClosePunchOut = useCallback(async (selectedVendor) => {
        setLoadingMaterials(true);
        await handleCloseDefineSupplierPopup(false, selectedVendor, undefined, false, false, []);
    }, [setLoadingMaterials]);

    const handleCloseDefineSupplierPopup = async (isOneTimeVendorSelected, genericVendor, OTVInfo, isCollectiveMedicalVendorSelected, isMultiTimeVendorSelected, attachments) => {
        setDefineSupplierPopupOpen(false);
        let newVendorCode;
        const [OTVCode, CMVCode, MTVCode] = getVendorCodes(Config.getParameter('ENV'));

        if (isOneTimeVendorSelected) {
            setVendorName(OTVInfo.name);
            setOTVAddress(
                {
                    name: '',
                    name2: OTVInfo.name2,
                    street: OTVInfo.street,
                    state: OTVInfo.state,
                    city: OTVInfo.city,
                    houseNumber: OTVInfo.houseNumber,
                    postalCode: OTVInfo.postalCode,
                    country: OTVInfo.country,
                });
            setOTVName(OTVInfo.name);
            setOTVPhoneNumber(OTVInfo.phoneNumber);
            setVendorCode(OTVCode);
            newVendorCode = OTVCode;
            setSupplierType(OneTimeVendor.supplierTypes.otv);
        } else if (genericVendor) {
            const idxOfHyphen = genericVendor.indexOf(' - ');
            setVendorCode(genericVendor.substring(0, idxOfHyphen));
            setVendorName(genericVendor.substring(idxOfHyphen + 3));
            newVendorCode = genericVendor.substring(0, idxOfHyphen);

            setOTVName('');
            setOTVPhoneNumber('');
            setOTVAddress({
                name: '',
                name2: '',
                houseNumber: '',
                street: '',
                state: '',
                city: '',
                postalCode: '',
                country: '',
            });
            setSupplierType(OneTimeVendor.supplierTypes.generic);
        } else if (isCollectiveMedicalVendorSelected) {
            setVendorName(OTVInfo.name);
            setOTVAddress(
                {
                    name: '',
                    name2: OTVInfo.name2,
                    street: OTVInfo.street,
                    state: OTVInfo.state,
                    city: OTVInfo.city,
                    houseNumber: OTVInfo.houseNumber,
                    postalCode: OTVInfo.postalCode,
                    country: OTVInfo.country,
                });
            setOTVName(OTVInfo.name);
            setOTVPhoneNumber(OTVInfo.phoneNumber);
            setVendorCode(CMVCode);
            newVendorCode = CMVCode;
            setSupplierType(OneTimeVendor.supplierTypes.cmv);
        } else if (isMultiTimeVendorSelected) {
            setVendorName(OTVInfo.name);
            setOTVAddress({
                name: '',
                name2: OTVInfo.name2,
                street: OTVInfo.street,
                state: OTVInfo.state,
                city: OTVInfo.city,
                houseNumber: OTVInfo.houseNumber,
                postalCode: OTVInfo.postalCode,
                country: OTVInfo.country,
            });
            setOTVName(OTVInfo.name);
            setOTVPhoneNumber(OTVInfo.phoneNumber);
            setVendorCode(MTVCode);
            newVendorCode = MTVCode;
            setSupplierType(OneTimeVendor.supplierTypes.mtv);
        }

        let incotermResult;
        try {
            incotermResult = await fetchAndProcessIncoterms({
                context: props.context,
                getIncoterms: getSupplierPurchaseOrgLink,
                getAllIncoterms: getAllIncoterms,
                vendorCode: newVendorCode,
                purchaseOrgValue: purchaseOrganization.value,
            });
        } catch (error) {
            console.error('Error fetching incoterms:', error);
            incotermResult = {code: '', description: ''};
        }
        const {code, description} = incotermResult;

        setIncoterms({
            data: {
                label: description,
                value: code,
            },
            manuallyModified: false,
        });
    };

    const addThreeDotsMenu = () => {
        let items = [];
        switch (currentTab) {
        case PurchaseRequest.tab.pendingSb:
            items = [
                {
                    title: 'Deny',
                    onClick: props.handleClickOnDeny,
                    disabled: props.disabledButtons.deny,
                },
                {
                    title: 'Approve',
                    onClick: props.handleClickOnApprove,
                    disabled: props.disabledButtons.approve,
                },
            ];
            break;
        case PurchaseRequest.tab.submitted:
            items = [
                {
                    title: 'Copy',
                    onClick: props.handleClickOnCopy,
                    disabled: props.disabledButtons.copy || isCandex,
                },
            ];
            break;
        case PurchaseRequest.tab.open:
            const showButtons = PurchaseRequestController.getAvailableActions(spender, requester, status);

            if (showButtons.rejectBtn && !isDisabled('actions.reject')) {
                items.push({
                    title: 'Reject',
                    onClick: props.handleClickOnReject,
                    disabled: props.disabledButtons.save,
                });
            }
            if (showButtons.saveBtn && !isDisabled('actions.save')) {
                items.push({
                    title: 'Save',
                    onClick: props.handleClickOnSave,
                    disabled: props.disabledButtons.save,
                });
            }
            if (showButtons.submitToRequesterBtn && !isDisabled('actions.submitToRequester')) {
                items.push({
                    title: 'Submit to Requester',
                    onClick: props.handleClickOnSubmitToRequester,
                    disabled: props.disabledButtons.save,
                });
            }
            if (showButtons.withdrawnBtn && !isDisabled('actions.withdrawn')) {
                items.push({
                    title: 'Withdrawn',
                    onClick: props.handleClickOnWithdrawn,
                    disabled: props.disabledButtons.save,
                });
            }
            if (showButtons.submitBtn && !isDisabled('actions.submit')) {
                items.push({
                    title: 'Submit',
                    onClick: props.handleClickOnSubmit,
                    disabled: props.disabledButtons.submit,
                });
            }
            items.push({
                title: 'Copy',
                onClick: props.handleClickOnCopy,
                disabled: props.disabledButtons.copy || isCandex,
            });
            break;
        }

        // Do not show Submit button if current user is not requester.

        return (
            <ThreeDotsMenu items={items} loading={props.loading} prTag={props.prTag}/>
        );
    };

    const mandatoryFieldsForRequesterLocal = companyCode.value.startsWith('CH') ?
        [...mandatoryFieldsForRequester, 'Supplier'] :
        mandatoryFieldsForRequester;

    const bgColorDSPIcon = (!companyCode.value.startsWith('CH') && mandatoryFieldsForSpender.includes('Supplier') && highlightMandatoryFieldForSpender) ?
        bgColorSpender :
        (mandatoryFieldsForRequesterLocal.includes('Supplier') && highlightMandatoryFieldForRequester) ?
            bgColorRequester :
            null;

    return (
        <div className={'pr-editor-title-sticky'}>
            <div className={'pr-editor-title-container'}>
                <div className={'pr-editor-title-left-hand-side'}>
                    <div className={'pr-editor-title-supplier'} style={{backgroundColor: bgColorDSPIcon}}>
                        <div>{vendorCode + ' -'}</div>
                        <div className={'pr-editor-title-supplier-name'} style={{color: ezprOrange}}>&nbsp;{vendorName}</div>
                        {editableSupplier && <IconButton
                            onClick={() => setDefineSupplierPopupOpen(true)}
                            sx={{
                                width: '24px',
                                height: '24px',
                                color: '#a1a0a0',
                                ml: '5px',
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                        }
                    </div>
                    <div style={{width: '30px'}}/>
                    <div className={'pr-editor-title-highlight-mandatory-fields'}>
                        <div className={'pr-editor-title-highlight-mandatory-fields-text'}>Mandatory fields for:</div>
                        <div className={'pr-editor-title-checkboxes'}>
                            <FormControlLabel
                                control={<Checkbox className={'pr-editor-title-checkbox-spender'} defaultChecked size={'small'} onClick={(event) => setHighlightMandatoryFieldForSpender(event.target.checked)} />}
                                label={<Typography className={'pr-editor-title-highlight-mandatory-fields-text'}>Spender</Typography>} />
                            <FormControlLabel
                                control={<Checkbox className={'pr-editor-title-checkbox-requester'} defaultChecked size={'small'} onClick={(event) => setHighlightMandatoryFieldForRequester(event.target.checked)} />}
                                label={<Typography className={'pr-editor-title-highlight-mandatory-fields-text'}>Requester</Typography>} />
                        </div>
                    </div>
                </div>

                {props.usersCheckingThePr?.length > 1 &&
                    <div className={'pr-editor-title-concurrent-users'}>
                        <IconWithTooltip icon={'group'} text={(<div>
                        Users checking the PR:
                            {props.usersCheckingThePr.map((u) => (<li key={u}>{`${u?.userFullName} - ${u?.userId?.toUpperCase()}`}</li>))}
                        </div>)} placement={'top'} style={{color: ezprOrange}}/>
                    </div>
                }

                <div className={'pr-editor-title-header'} style={{marginLeft: props.usersCheckingThePr?.length > 1 ? '0px': 'auto'}} ref={props.headerRef}>
                    <div
                        className={`pr-editor-title-tab-selector-link generalDetails-link-${props.prTag}`}
                        onClick={() => {
                            props.scrollTo(props.generalDetailsRef.current);
                        }}
                    >
                        General details
                    </div>
                    <div
                        className={`pr-editor-title-tab-selector-link itemDetails-link-${props.prTag}`}
                        onClick={() => {
                            props.scrollTo(props.itemDetailsRef.current);
                        }}
                    >
                        Item<br/>details
                    </div>
                    <div
                        className={`pr-editor-title-tab-selector-link accountDetails-link-${props.prTag}`}
                        onClick={() => {
                            props.scrollTo(props.accountDetailsRef.current);
                        }}
                    >
                        Account details
                    </div>
                    <div
                        className={`pr-editor-title-tab-selector-link poSendingOptions-link-${props.prTag}`}
                        onClick={() => {
                            props.scrollTo(props.poSendingOptionsRef.current);
                        }}
                    >
                        PO Sending options
                    </div>
                    <div
                        className={`pr-editor-title-tab-selector-link attachmentsAndComments-link-${props.prTag}`}
                        onClick={() => {
                            props.scrollTo(props.attachmentsAndCommentsRef.current);
                        }}
                    >
                        Attachments & comments
                    </div>
                </div>

                <div
                    className={'pr-editor-title-compress-button'}
                    data-cy={'close-pr-button'}
                    onClick={props.handleClickOnExpand}
                >
                    <i className="material-icons" >{'expand_more'}</i>
                    {(props.currentTab === PurchaseRequest.tab.pendingSb || userHasAccessToProperties) && addThreeDotsMenu()}
                </div>
            </div>
            <DefineSupplierPopup
                onClose={handleCloseDefineSupplierPopup}
                onCancel={handleCancelDefineSupplierPopup}
                open={defineSupplierPopupOpen}
                selectedValue={undefined}
                context={props.context}
                prTag={props.prTag}
                prLocked={prLocked}
                onClosePunchOut={handleClosePunchOut}
            />
        </div>
    );
}

TabSelector.propTypes = {
    prTag: PropTypes.string,
    currentTab: PropTypes.string,
    handleClickOnExpand: PropTypes.func,
    scrollTo: PropTypes.func,
    headerRef: PropTypes.object,
    generalDetailsRef: PropTypes.object,
    itemDetailsRef: PropTypes.object,
    accountDetailsRef: PropTypes.object,
    poSendingOptionsRef: PropTypes.object,
    attachmentsAndCommentsRef: PropTypes.object,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    handleClickOnDeny: PropTypes.func.isRequired,
    handleClickOnApprove: PropTypes.func.isRequired,
    handleClickOnSave: PropTypes.func.isRequired,
    handleClickOnSubmit: PropTypes.func.isRequired,
    handleClickOnSubmitToRequester: PropTypes.func.isRequired,
    handleClickOnWithdrawn: PropTypes.func.isRequired,
    handleClickOnReject: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    handleClickOnCopy: PropTypes.func.isRequired,
    disabledButtons: PropTypes.object,
    usersCheckingThePr: PropTypes.array,
};
